import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import IconButton from 'apollo-react/components/IconButton';
import Modal from 'apollo-react/components/Modal';
import TextField from 'apollo-react/components/TextField';
import Tooltip from 'apollo-react/components/Tooltip';
import Typography from 'apollo-react/components/Typography';
import { Refresh } from 'apollo-react-icons';
import CopyIcon from 'apollo-react-icons/Copy';
import classNames from 'classnames';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';

import ButtonLoader from '../../components/general/Login/LoginLoader';
import { useFormSelector } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getAppSettingsByKey } from '../../selectors/AppConfigsSelectors';
import { checkTrue, getUITA } from '../../utils/commonUtils';
import { DATE_FORMAT_WITH_MINUTES_TIMESTAMP } from '../../utils/dateUtils';
import { fetchAISummary } from '../actions/runtime.actions';
import { FORM_MODEL_ATTRIBUTES } from '../constants/form-manager.constants';
import { AI_SUMMARY_APP_SETTING_KEY } from '../constants/runtime.constants';
import { FormContext } from '../context';
import {
  getEvaluatedConditions,
  getEvaluatedExpression,
  getPropByModel,
} from '../selectors/form-manager.selectors';

const useStyles = makeStyles({
  requiredIcon: {
    '& label > span': { position: 'static !important' },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    width: ({ headerWidth }) => headerWidth,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
    backgroundColor: '#f6f7fb',
    border: '1px solid #e9e9e9;',
    borderBottom: 'none',
  },
  button: {
    minWidth: 150,
  },
  label: {
    fontSize: 14,
    marginRight: 15,
    paddingBottom: 5,
    color: '#333333',
    position: 'relative',
  },
  extraLabelPadding: {
    paddingBottom: 12,
  },
  asterisk: {
    top: '-1px',
    color: '#bd0100',
    width: 22,
    position: 'absolute',
    fontSize: 20,
    fontWeight: 'bold',
    lineHeight: 1,
    marginLeft: 4,
  },
  textarea: {
    minWidth: 0,
    marginTop: 0,
    '& [class*="MuiFormLabel"]': { padding: 0 },
  },
  headerSubContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  copyIcon: {
    paddingTop: 4,
    marginLeft: 5,
  },
});

const DEFAULT_CONTROL_WIDTH = 568;

const SSSummary = ({
  label,
  actions,
  value = '',
  isRequired,
  className,
  error,
  isHiddenLabel,
  instanceId,
  entityName,
  fieldName,
  isEditor,
  disabled,
  sourceFields,
  id,
  index,
  extraPrompts,
  summaryGenerationDate,
}) => {
  const [newValue, setValue] = useState(value);
  const [isLoading, setLoading] = useState(false);
  const [isWarningModalOpen, setWarningModalOpen] = useState(false);
  const [copyTooltipText, setCopyTooltipText] = useState('Copy');
  const [headerWidth, setHeaderWidth] = useState(DEFAULT_CONTROL_WIDTH);

  const formKey = useContext(FormContext);

  const classes = useStyles({ headerWidth });
  const translateString = useTranslation();
  const dispatch = useDispatch();
  const textAreaRef = useRef(null);

  const showPostSummaryMessage = useFormSelector(getEvaluatedConditions, {
    id,
    index,
    entityName,
    prop: FORM_MODEL_ATTRIBUTES.SHOW_POST_SUMMARY_GENERATION_MESSAGE,
  });
  const postSummaryMessage = useFormSelector(getEvaluatedExpression, {
    id,
    index,
    prop: FORM_MODEL_ATTRIBUTES.POST_SUMMARY_GENERATION_MESSAGE,
  });
  const lastGeneratedDate = useFormSelector(getEvaluatedExpression, {
    id,
    index,
    prop: FORM_MODEL_ATTRIBUTES.SUMMARY_GENERATION_DATE,
  });
  const formPropLabel = useFormSelector(getPropByModel, {
    id,
    prop: FORM_MODEL_ATTRIBUTES.LABEL,
  });

  const onResize = useCallback((width) => {
    setHeaderWidth(width + 18);
  }, []);

  useResizeDetector({
    onResize,
    handleHeight: false,
    targetRef: textAreaRef,
    refreshRate: 200,
  });

  useEffect(() => {
    setValue(value);
  }, [value]);

  const onBlur = ({ target: { value: v } }) => {
    const _value = v.trim();
    setValue(_value);

    if (value !== _value) {
      actions.updateControlValue(_value);
    }
  };

  const handleDataFetch = async () => {
    setLoading(true);
    const { hasError, summary } = await dispatch(
      fetchAISummary({
        sourceFields,
        showPostSummaryMessage,
        extraPrompts,
        formKey,
        summaryGenerationDate,
        index,
        postSummaryMessage,
        entityName,
      })
    );

    if (!hasError) {
      actions.updateControlValue(summary);
    }
    setLoading(false);
  };

  const onGenerateSummaryClick = async () => {
    setWarningModalOpen(false);
    handleDataFetch();
  };

  const handleGenerateSummaryClick = () => {
    value ? setWarningModalOpen(true) : handleDataFetch();
  };

  const handleClickCopy = () => {
    setCopyTooltipText('Copied');
    navigator.clipboard.writeText(newValue);

    setTimeout(() => {
      setCopyTooltipText('Copy');
    }, 1000);
  };

  const summaryEnabledFlag = useSelector(getAppSettingsByKey(AI_SUMMARY_APP_SETTING_KEY));
  const isSummaryEnabled = checkTrue(summaryEnabledFlag) || isEditor;

  const inputTitle =
    isRequired &&
    !value &&
    translateString(
      `Please fill out this field${isSummaryEnabled ? " or use 'Generate Summary' button" : ''}`
    );

  const labelToDisplay = isHiddenLabel || (isSummaryEnabled && !formPropLabel) ? '' : label;

  const dateValue =
    lastGeneratedDate && moment(lastGeneratedDate).isValid()
      ? moment(lastGeneratedDate).format(DATE_FORMAT_WITH_MINUTES_TIMESTAMP)
      : '';
  const dateLabel = isEditor
    ? 'AI drafted date'
    : dateValue
    ? `${translateString('AI drafted on')}: ${dateValue}`
    : '';

  return (
    <div
      className={classes.container}
      ui-ta={getUITA('FormCtrl', 'SSSummary', entityName, fieldName)}
    >
      <label
        className={classNames({
          [classes.label]: true,
          [classes.extraLabelPadding]: !labelToDisplay,
        })}
      >
        {labelToDisplay}
        {isRequired && <span className={classes.asterisk}>{'*'}</span>}
      </label>
      {isSummaryEnabled && (
        <div className={classes.header}>
          <Tooltip
            title={isLoading ? translateString('Generate Summary pending') : ''}
            placement="bottom"
          >
            <span>
              <Button
                variant="primary"
                icon={!isLoading ? <Refresh /> : null}
                onClick={handleGenerateSummaryClick}
                className={classes.button}
                disabled={isLoading || isEditor}
                ui-ta={getUITA('generateSummaryButton')}
              >
                {isLoading ? <ButtonLoader size={26} /> : translateString('Generate Summary')}
              </Button>
            </span>
          </Tooltip>
          <div className={classes.headerSubContainer} ui-ta={getUITA('draftedContainer')}>
            {dateLabel && <Typography ui-ta={getUITA('draftedLabel')}>{dateLabel}</Typography>}
            {newValue && (
              <Tooltip
                title={translateString(copyTooltipText)}
                className={classes.copyIcon}
                disableFocusListener
              >
                <IconButton
                  onClick={handleClickCopy}
                  disabled={isEditor}
                  ui-ta={getUITA('draftedCopyButton')}
                >
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>
      )}
      <TextField
        id={instanceId}
        fullWidth
        multiline
        value={newValue ?? ''}
        className={classNames(className, classes.requiredIcon, classes.textarea)}
        onBlur={onBlur}
        onChange={({ target: { value } }) => setValue(value)}
        error={error}
        inputProps={{
          rows: 2,
          style: {
            height: 'auto',
            overflow: 'auto',
            resize: 'both',
            ...(isSummaryEnabled && {
              minHeight: 150,
              width: DEFAULT_CONTROL_WIDTH,
              minWidth: 400,
              borderRadius: 0,
            }),
          },
          title: inputTitle,
          'aria-label': translateString(label),
          ref: isSummaryEnabled && textAreaRef,
        }}
        disabled={isEditor || disabled}
        sizeAdjustable
      />
      <Modal
        open={isWarningModalOpen}
        onClose={() => setWarningModalOpen(false)}
        variant="warning"
        title={translateString('Warning')}
        message={translateString(
          'Updates to summary will be lost. Are you sure you want to regenerate?'
        )}
        disableBackdropClick
        disableEscapeKeyDown
        buttonProps={[
          {
            label: translateString('No'),
            'ui-ta': getUITA('PopupButtonNO'),
          },
          {
            variant: 'primary',
            label: translateString('Yes'),
            onClick: onGenerateSummaryClick,
            'ui-ta': getUITA('PopupButtonYES'),
          },
        ]}
        PaperProps={{ 'ui-ta': getUITA('ConfirmPopupElement') }}
      />
    </div>
  );
};

export default SSSummary;
